import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Typography,
  Paper,
  useTheme,
  alpha,
  Tooltip,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import ConfirmationDialog from 'components/ConfirmationDialog';
import ConfigFormDialog from './ConfigFormDialog';
import { regionSyncService } from '../services/RegionSyncService';
import { SyncConfig, ConnectionTestResult } from '../types';
import { logger } from 'utils/logger';

const log = logger.getLogger('ConfigTab');

const ConfigTab: React.FC = () => {
  const theme = useTheme();
  const [configs, setConfigs] = useState<SyncConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SyncConfig | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<SyncConfig | null>(null);

  // Connection test states
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [connectionResults, setConnectionResults] = useState<Record<number, ConnectionTestResult>>({});

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await regionSyncService.getConfigs();
      setConfigs(data);
    } catch (err: any) {
      log.error('Failed to fetch configs:', err);
      setError(err.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleAddConfig = () => {
    setEditingConfig(null);
    setFormDialogOpen(true);
  };

  const handleEditConfig = (config: SyncConfig) => {
    setEditingConfig(config);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (config: SyncConfig) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;

    try {
      await regionSyncService.deleteConfig(configToDelete.id);
      setConfigs(configs.filter(c => c.id !== configToDelete.id));
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    } catch (err: any) {
      log.error('Failed to delete config:', err);
      setError(err.message || 'Failed to delete configuration');
    }
  };

  const handleTestConnection = async (config: SyncConfig) => {
    try {
      setTestingConnection(config.id);
      const result = await regionSyncService.testConnection(config.id);
      setConnectionResults(prev => ({ ...prev, [config.id]: result }));
    } catch (err: any) {
      log.error('Connection test failed:', err);
      setConnectionResults(prev => ({
        ...prev,
        [config.id]: { success: false, message: err.message || 'Connection failed' }
      }));
    } finally {
      setTestingConnection(null);
    }
  };

  const handleFormSave = async () => {
    setFormDialogOpen(false);
    setEditingConfig(null);
    await fetchConfigs();
  };

  const getEnvironmentColor = (env: string) => {
    const colors: Record<string, string> = {
      'prod': theme.palette.success.main,
      'staging': theme.palette.warning.main,
      'dev': theme.palette.secondary.main,
    };
    return colors[env] || theme.palette.grey[500];
  };

  // Header cell style matching History tab
  const headerCellSx = {
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    py: 1.5,
    '&:hover': {
      color: theme.palette.text.primary
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Fade in={true} timeout={600}>
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
            }
          }}
        >
          {/* Header / Toolbar */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}
              >
                Database Configurations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Manage source and target database connections for region sync operations.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddConfig}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2.5,
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                }
              }}
            >
              Add Configuration
            </Button>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Name</TableCell>
                  <TableCell sx={headerCellSx}>Environment</TableCell>
                  <TableCell sx={headerCellSx}>GCP Project</TableCell>
                  <TableCell sx={headerCellSx}>Host</TableCell>
                  <TableCell sx={headerCellSx}>Database</TableCell>
                  <TableCell sx={headerCellSx}>Auth</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No configurations found. Click &quot;Add Configuration&quot; to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  configs.map((config) => (
                    <TableRow
                      key={config.id}
                      sx={{
                        transition: 'all 0.15s ease-in-out',
                        '&:nth-of-type(odd)': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.02)
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.06),
                          transform: 'translateY(-1px)',
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`
                        }
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight={500}>{config.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={config.environment}
                          size="small"
                          sx={{
                            backgroundColor: alpha(getEnvironmentColor(config.environment), 0.1),
                            color: getEnvironmentColor(config.environment),
                            fontWeight: 500,
                            borderRadius: '8px'
                          }}
                        />
                      </TableCell>
                      <TableCell>{config.gcpProject || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {config.dbHost}:{config.dbPort}
                        </Typography>
                      </TableCell>
                      <TableCell>{config.dbName}</TableCell>
                      <TableCell>
                        <Chip
                          label={config.k8sSecretName ? 'K8s Secret' : 'Stored'}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: '8px', fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        {connectionResults[config.id] ? (
                          connectionResults[config.id].success ? (
                            <Tooltip title={connectionResults[config.id].message}>
                              <CheckCircleIcon color="success" />
                            </Tooltip>
                          ) : (
                            <Tooltip title={connectionResults[config.id].message}>
                              <ErrorIcon color="error" />
                            </Tooltip>
                          )
                        ) : (
                          <Chip
                            label={config.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={config.isActive ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ borderRadius: '8px', fontWeight: 500 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Test Connection">
                            <IconButton
                              size="small"
                              onClick={() => handleTestConnection(config)}
                              disabled={testingConnection === config.id}
                              sx={{
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                }
                              }}
                            >
                              {testingConnection === config.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <PlayArrowIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditConfig(config)}
                              sx={{
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  backgroundColor: alpha(theme.palette.info.main, 0.08)
                                }
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(config)}
                              color="error"
                              sx={{
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  backgroundColor: alpha(theme.palette.error.main, 0.08)
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Fade>

      {/* Config Form Dialog */}
      <ConfigFormDialog
        open={formDialogOpen}
        config={editingConfig}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingConfig(null);
        }}
        onSave={handleFormSave}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Configuration"
        content={`Are you sure you want to delete "${configToDelete?.name}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        severity="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setConfigToDelete(null);
        }}
      />
    </Box>
  );
};

export default ConfigTab;
